import React, { useMemo } from 'react';
import { Spinner } from 'react-bootstrap';

import InteractiveMap, {
  ESRI_SATELLITE_TILE_ATTRIBUTION,
  ESRI_SATELLITE_TILE_URL,
} from 'components/InteractiveMap';
import BrazilStatesLayer from 'components/InteractiveMap/BrazilStatesLayer';
import BrazilCountryLayer from 'components/InteractiveMap/BrazilCountryLayer';
import CanvasHeatLayer from 'components/InteractiveMap/CanvasHeatLayer';
import AgroMapHoverCard from 'components/Meteorology/Agro/Maps/AgroMapHoverCard';
import AgroMapLayersPanel from 'components/Meteorology/Agro/Maps/AgroMapLayersPanel';
import QueryErrorState from 'components/DesignSystem/QueryErrorState';

import useMapData from 'hooks/Meteorology/Agro/WaterBalanceSequential/Maps/useMapData';
import useMapLocationMarkers from 'hooks/Meteorology/Agro/WaterBalanceSequential/Maps/useMapLocationMarkers';
import useCaption from 'hooks/Meteorology/Agro/WaterBalanceSequential/Maps/useCaption';
import useEnrichedMarkers from 'hooks/Meteorology/Agro/WaterBalanceSequential/Maps/useEnrichedMarkers';
import useVariables from 'hooks/Meteorology/Agro/WaterBalanceSequential/Maps/useVariables';
import useHeatmapLegend from 'hooks/Meteorology/Agro/WaterBalanceSequential/Maps/useHeatmapLegend';
import useAgroMapState from 'hooks/Meteorology/Agro/Maps/useAgroMapState';

import { findNearestValue } from 'helper/Meteorology/Agro/Maps/geoUtils';

const LAYER_DEFS = [
  { key: 'localidades', label: 'Localidades' },
  { key: 'limitesEstaduais', label: 'Limites estaduais' },
  { key: 'tooltip', label: 'Tooltip de informações' },
];

export default function MapDisplay({
  setOnDownloadClick,
  mapFilename,
}: {
  setOnDownloadClick: React.Dispatch<React.SetStateAction<() => void>>;
  mapFilename: string;
}) {
  const {
    mapData, queryError, onRetry, loading,
  } = useMapData();
  const locationMarkers = useMapLocationMarkers();
  const caption = useCaption();
  const { variableLabel, unit, config } = useVariables();
  const legend = useHeatmapLegend(mapData);
  const enrichedMarkers = useEnrichedMarkers(locationMarkers, mapData, variableLabel ?? '', unit);

  const {
    layers, handleToggle,
    cursorPos, handleMove, handleLeave,
    hoveredState, handleStateHover, handleStateLeave,
    isOverMarker, handleMarkerEnter, handleMarkerLeave,
  } = useAgroMapState(LAYER_DEFS);

  const nearestValue = useMemo(() => {
    if (!cursorPos || !mapData?.length) return null;
    return findNearestValue(cursorPos.lat, cursorPos.lng, mapData);
  }, [cursorPos, mapData]);

  return (
    <div className="position-relative">
      <InteractiveMap
        markers={layers.localidades ? enrichedMarkers : []}
        tileUrl={ESRI_SATELLITE_TILE_URL}
        tileAttribution={ESRI_SATELLITE_TILE_ATTRIBUTION}
        onMarkerEnter={handleMarkerEnter}
        onMarkerLeave={handleMarkerLeave}
      >
        <CanvasHeatLayer
          points={(!loading && mapData) || []}
          config={config}
          legend={legend}
        />
        {layers.limitesEstaduais
          ? (
            <BrazilStatesLayer
              onStateHover={handleStateHover}
              onStateLeave={handleStateLeave}
            />
          )
          : <BrazilCountryLayer />}
        <InteractiveMap.MapHoverTracker
          onMove={handleMove}
          onLeave={handleLeave}
        />
        <InteractiveMap.ImageDownloader
          setOnDownload={setOnDownloadClick}
          filename={mapFilename}
          caption={caption}
        />
      </InteractiveMap>

      {queryError && (
        <div className="agro-map-error">
          <QueryErrorState
            title={queryError.data}
            message={queryError.description}
            onRetry={onRetry}
          />
        </div>
      )}

      <AgroMapLayersPanel
        layers={layers}
        onToggle={handleToggle}
        definitions={LAYER_DEFS}
      />

      {loading && (
        <div className="agro-map-loading">
          <Spinner animation="border" />
        </div>
      )}

      <AgroMapHoverCard
        cursorX={isOverMarker || !layers.tooltip ? null : cursorPos?.x ?? null}
        cursorY={isOverMarker || !layers.tooltip ? null : cursorPos?.y ?? null}
        value={nearestValue}
        label={variableLabel ?? ''}
        unit={unit}
        stateName={hoveredState?.name ?? null}
      />
    </div>
  );
}
